import React, { useEffect, useState } from "react";
import homeIcon from "../../assets/images/homepage/homeIcon.svg";
import interviewIcon from "../../assets/images/homepage/interviewIcon.svg";
import historyIcon from "../../assets/images/homepage/historyIcon.svg";
import { ChevronLeft, ChevronRight, Trophy } from "lucide-react";
import usePartnerAwareNavigate from "src/hooks/usePartnerAwareNavigate";
import { useLocation } from "react-router-dom";

type TabType = "home" | "interviews" | "assessmentHistory" | "weeklyContest";

type NavigationProps = {
  activeTab: TabType;
  setActiveTab: React.Dispatch<React.SetStateAction<TabType>>;
  isCollapsed: boolean;
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
};

interface NavItem {
  id: TabType;
  label: string;
  icon: string | React.ReactNode;
  path: string;
  isVisible?: (companyName: string) => boolean;
}

const navItems: NavItem[] = [
  { id: "home", label: "Home", icon: homeIcon, path: "/" },
  {
    id: "interviews",
    label: "Practice",
    icon: interviewIcon,
    path: "/interviews",
  },
  // {
  //     id: "interviewHistory",
  //     label: "Interview History",
  //     icon: historyIcon,
  //     path: "/interview-history",
  // },
  // {
  //     id: "caseReports",
  //     label: "Case Reports",
  //     icon: caseReportsIcon,
  //     path: "/case-reports",
  // },
  {
    id: "assessmentHistory",
    label: "Assessment History",
    icon: historyIcon,
    path: "/assessment-history",
  },
  {
    id: "weeklyContest",
    label: "Mock Case Competition",
    icon: <Trophy className="w-5 h-5" />,
    path: "/weekly-contest",
    isVisible: (companyName) =>
      companyName === "ucla-bsn" || companyName === "babson-bca",
  },
];

export const Navigation: React.FC<NavigationProps> = ({
  activeTab,
  setActiveTab,
  isCollapsed,
  setIsCollapsed,
}) => {
  const [viewportHeight, setViewportHeight] = useState(0);
  const navigate = usePartnerAwareNavigate();
  const location = useLocation();

  // Get company name from URL
  const segments = location.pathname.split("/").filter(Boolean);
  const companyName = segments[0];

  // Get and update viewport height on mount and resize
  useEffect(() => {
    const updateViewportHeight = () => {
      setViewportHeight(window.innerHeight);
    };

    // Set initial height
    updateViewportHeight();

    // Add resize listener
    window.addEventListener("resize", updateViewportHeight);

    // Cleanup
    return () => window.removeEventListener("resize", updateViewportHeight);
  }, []);

  const baseNavStyles =
    "fixed top-16 left-0  bg-white shadow-[2px_0_5px_rgba(0,0,0,0.05)] transition-all duration-300 ease-in-out z-10 group/nav bottom-0";
  const navWidth = isCollapsed ? "w-[72px]" : "w-[240px]";

  const baseButtonStyles =
    "relative group flex items-center px-6 py-3.5 w-full transition-all duration-200 hover:bg-[#F5F6F7] before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-9 before:w-1 before:opacity-0 before:bg-[#423ED8] before:transition-opacity before:duration-200 hover:before:opacity-100";

  const getActiveStyles = (isActive: boolean) => ({
    button: isActive ? "bg-[#F5F6F7] text-[#423ED8]" : "text-[#64748B]",
    icon: isActive ? "opacity-100" : "opacity-70 group-hover:opacity-100",
  });

  const handleNavClick = (item: NavItem) => {
    setActiveTab(item.id);
    navigate(item.path);
  };

  return (
    <nav className={`${baseNavStyles} ${navWidth}`}>
      {/* Toggle Button - aligned with nav items */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className={`
          w-full
          flex
          items-center
          px-6
          py-3.5
          transition-all
          duration-200
          opacity-40
          hover:opacity-100
          group-hover/nav:opacity-100
          hover:bg-[#F5F6F7]
          focus:outline-none
          focus-visible:ring-2
          focus-visible:ring-[#423ED8]
          cursor-pointer
          text-[#64748B]
          hover:text-[#423ED8]
        `}
        aria-label={isCollapsed ? "Expand navigation" : "Collapse navigation"}
      >
        <div className="flex items-center justify-center w-5 h-5">
          {isCollapsed ? (
            <ChevronRight className="w-5 h-5 transition-colors duration-200" />
          ) : (
            <ChevronLeft className="w-5 h-5 transition-colors duration-200" />
          )}
        </div>
        {!isCollapsed && (
          <span className="ml-3 font-medium text-[15px]">Collapse Menu</span>
        )}
      </button>

      <div className="py-4">
        {navItems
          .filter((item) => !item.isVisible || item.isVisible(companyName))
          .map((item) => {
            const styles = getActiveStyles(activeTab === item.id);
            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                className={`${baseButtonStyles} ${styles.button}`}
              >
                {typeof item.icon === "string" ? (
                  <img
                    src={item.icon}
                    className={`w-5 h-5 ${styles.icon}`}
                    alt={`${item.label} icon`}
                    style={{
                      filter:
                        "invert(25%) sepia(97%) saturate(1944%) hue-rotate(235deg) brightness(94%) contrast(89%)",
                    }}
                  />
                ) : (
                  <div
                    className={`w-5 h-5 ${styles.icon}`}
                    style={{
                      color: "var(--color-primary)",
                    }}
                  >
                    {item.icon}
                  </div>
                )}
                {!isCollapsed && (
                  <span className="ml-3 font-medium text-[15px]">
                    {item.label}
                  </span>
                )}
              </button>
            );
          })}
      </div>
    </nav>
  );
};

export default Navigation;
